import {
  Button,
  ColorInput,
  FileInput,
  Group,
  Image,
  Input,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import {
  AdminForm,
  AdminFormActions,
  AdminFormModal,
  AdminFormSection,
} from "../../../components/AdminForm.tsx";
import type { CreateForm } from "./types.ts";
import { genPrizeCode, PRIZE_TYPES } from "./types.ts";

type Props = {
  opened: boolean;
  onClose: () => void;
  form: UseFormReturnType<CreateForm>;
  isPending: boolean;
  imageUploading: boolean;
  onSubmit: (v: CreateForm) => void;
  onImageUpload: (file: File | null) => void;
  languages: readonly string[];
  existingCodes: ReadonlySet<string>;
};

export function CreatePrizeModal({
  opened,
  onClose,
  form,
  isPending,
  imageUploading,
  onSubmit,
  onImageUpload,
  languages,
  existingCodes,
}: Props) {
  const type = form.values.type;

  const codeError = ((): string | null => {
    const v = form.values.code.trim();
    if (v.length === 0) return null; // 空时不提前报错，交给 submit validate
    if (existingCodes.has(v)) return "该 code 已被现有奖品占用（含已禁用），请换一个";
    return null;
  })();

  return (
    <AdminFormModal title="添加奖品" opened={opened} onClose={onClose} size="xl">
      <AdminForm onSubmit={form.onSubmit(onSubmit)}>
        <AdminFormSection title="基本信息">
          <SimpleGrid cols={2}>
            <Group wrap="nowrap" align="flex-end" gap="xs">
              <TextInput
                label="code"
                required
                style={{ flex: 1 }}
                {...form.getInputProps("code")}
                error={form.getInputProps("code").error ?? codeError}
              />
              <Button
                size="xs"
                variant="light"
                type="button"
                onClick={() => form.setFieldValue("code", genPrizeCode())}
              >
                重新生成
              </Button>
            </Group>
            <Select label="类型" data={PRIZE_TYPES} required {...form.getInputProps("type")} />
            <TextInput label="名称" required {...form.getInputProps("name")} />
            <NumberInput label="权重" min={0} required {...form.getInputProps("weight")} />
          </SimpleGrid>
          <Textarea
            label="描述（可选，运营备注）"
            rows={2}
            {...form.getInputProps("description")}
          />
        </AdminFormSection>

        <AdminFormSection title="价值与库存">
          <SimpleGrid cols={2}>
            <NumberInput
              label={type === "extra_spins" ? "价值（赠送抽奖次数）" : "价值（金额）"}
              min={0}
              {...form.getInputProps("valueAmount")}
            />
            <TextInput
              label="货币（virtual / physical 现金奖填，如 INR）"
              placeholder="INR"
              {...form.getInputProps("valueCurrency")}
            />
            <TextInput label="库存（空为无限）" {...form.getInputProps("stock")} />
            <TextInput label="日上限（空为无限）" {...form.getInputProps("dailyLimit")} />
          </SimpleGrid>
          <Switch
            label="设为大奖（jackpot：中奖触发广播 + 大奖弹窗）"
            checked={form.values.isJackpot}
            onChange={(e) => form.setFieldValue("isJackpot", e.currentTarget.checked)}
          />
        </AdminFormSection>

        {type === "virtual" || type === "physical" ? (
          <AdminFormSection title="派彩与领奖">
            {type === "virtual" ? (
              <Switch
                label="WG Pay 自动派彩（virtual 现金奖，绑定用户中奖即派彩）"
                checked={form.values.wgPay}
                onChange={(e) => form.setFieldValue("wgPay", e.currentTarget.checked)}
              />
            ) : null}
            {type === "virtual" && form.values.wgPay ? (
              <NumberInput
                label="稽核倍率（WG 流水要求倍数，1 = 无要求）"
                min={1}
                step={0.5}
                decimalScale={2}
                {...form.getInputProps("auditRate")}
              />
            ) : null}
            {languages.map((lang) => (
              <Textarea
                key={`ci-${lang}`}
                label={`领奖说明 · ${lang}（展示在中奖弹窗）`}
                rows={2}
                value={form.values.claimInstructions[lang] ?? ""}
                onChange={(e) =>
                  form.setFieldValue("claimInstructions", {
                    ...form.values.claimInstructions,
                    [lang]: e.currentTarget.value,
                  })
                }
              />
            ))}
            {type === "physical" ? (
              <Switch
                label="实物需要收件"
                checked={form.values.requiresShipping}
                onChange={(e) => form.setFieldValue("requiresShipping", e.currentTarget.checked)}
              />
            ) : null}
          </AdminFormSection>
        ) : null}

        <AdminFormSection title="扇区展示">
          <Input.Wrapper
            label="标题模式"
            description="文字 = 扇区展示名称；图片 = 仅展示上传的图片；图片+文字 = 图片 + 名称同时展示"
          >
            <SegmentedControl
              fullWidth
              mt={4}
              data={[
                { label: "文字", value: "text" },
                { label: "图片", value: "image" },
                { label: "图片+文字", value: "image_text" },
              ]}
              value={form.values.titleMode}
              onChange={(v) =>
                form.setFieldValue("titleMode", v as "text" | "image" | "image_text")
              }
            />
          </Input.Wrapper>
          <FileInput
            label="图片（图片 / 图片+文字 模式生效）"
            placeholder="选择图片文件"
            accept="image/*"
            disabled={imageUploading}
            onChange={(file) => onImageUpload(file)}
          />
          {form.values.imageUrl !== "" ? (
            <Group gap="sm" align="center">
              <Image src={form.values.imageUrl} h={60} w={60} fit="contain" />
              <Button
                variant="light"
                color="red"
                size="xs"
                type="button"
                onClick={() => form.setFieldValue("imageUrl", "")}
              >
                清除图片
              </Button>
            </Group>
          ) : null}
          {languages.map((lang) => (
            <TextInput
              key={lang}
              label={`名称 · ${lang}（扇区文字，留空回退「名称」）`}
              value={form.values.i18nName[lang] ?? ""}
              onChange={(e) =>
                form.setFieldValue("i18nName", {
                  ...form.values.i18nName,
                  [lang]: e.currentTarget.value,
                })
              }
            />
          ))}
          {languages.map((lang) => (
            <TextInput
              key={`sub-${lang}`}
              label={`副标题 · ${lang}（扇区小字；图片+文字 模式不显示）`}
              value={form.values.i18nSubtitle[lang] ?? ""}
              onChange={(e) =>
                form.setFieldValue("i18nSubtitle", {
                  ...form.values.i18nSubtitle,
                  [lang]: e.currentTarget.value,
                })
              }
            />
          ))}
          <SimpleGrid cols={3}>
            <ColorInput label="背景色" {...form.getInputProps("displayColor")} />
            <TextInput
              label="图标字符（如 ★，避免 emoji）"
              {...form.getInputProps("displayIcon")}
            />
            <NumberInput
              label="扇区顺序（小在前）"
              min={0}
              {...form.getInputProps("displayOrder")}
            />
          </SimpleGrid>
        </AdminFormSection>

        <AdminFormSection title="上下架时间">
          <SimpleGrid cols={2}>
            <TextInput
              type="datetime-local"
              label="上架时间（可空，不填则立即上架）"
              {...form.getInputProps("startsAt")}
            />
            <TextInput
              type="datetime-local"
              label="下架时间（可空，不填则永久有效）"
              {...form.getInputProps("endsAt")}
            />
          </SimpleGrid>
        </AdminFormSection>

        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" loading={isPending || imageUploading}>
            保存
          </Button>
        </AdminFormActions>
      </AdminForm>
    </AdminFormModal>
  );
}
